import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Settings,
  Plus,
  Save,
  Eye,
  Trash2,
  GripVertical,
  Edit,
  AlertCircle,
  FileText,
  List,
  Users,
  Layers,
  Loader2,
  Check,
  X,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const errorMessage = error => typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Änderung konnte nicht gespeichert werden";

const FIELD_TYPES = {
  text: "Kurzes Textfeld",
  textarea: "Langes Textfeld",
  number: "Zahl",
  date: "Datum",
  time: "Uhrzeit",
  datetime: "Datum und Uhrzeit",
  boolean: "Ja/Nein",
  checkbox: "Checkbox",
  select: "Einfachauswahl (Dropdown)",
  multiselect: "Mehrfachauswahl",
  radio: "Einfachauswahl (Radio)",
  file: "Dateiupload",
  image: "Bildupload"
};

export default function FormBuilder() {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [meldungsarten, setMeldungsarten] = useState([]);
  const [selectedMeldungsart, setSelectedMeldungsart] = useState(null);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [optionLists, setOptionLists] = useState([]);
  const [activeTab, setActiveTab] = useState("meldungsarten");
  
  // Dialoge
  const [newMeldungsartDialog, setNewMeldungsartDialog] = useState(false);
  const [newFieldDialog, setNewFieldDialog] = useState(false);
  const [editFieldDialog, setEditFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [newOptionListDialog, setNewOptionListDialog] = useState(false);
  const [editOptionListDialog, setEditOptionListDialog] = useState(false);
  const [editingOptionList, setEditingOptionList] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  
  // Form States
  const [newMeldungsartName, setNewMeldungsartName] = useState("");
  const [newMeldungsartDesc, setNewMeldungsartDesc] = useState("");
  
  const [newField, setNewField] = useState({
    field_key: "",
    field_type: "text",
    label: "",
    placeholder: "",
    help_text: "",
    is_required: false,
    option_list_id: "",
    inline_options: ""
  });
  
  const [newOptionList, setNewOptionList] = useState({
    name: "",
    display_name: "",
    description: "",
    options: ""
  });
  
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const companyRef = useRef(null);
  const typeRef = useRef(null);
  companyRef.current = selectedCompany?.id;
  typeRef.current = selectedMeldungsart?.id;

  // Data Fetching
  const fetchUserAndCompanies = useCallback(async () => {
    try {
      const companiesResponse = await axios.get(`${API}/companies`);
      setCompanies(companiesResponse.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMeldungsarten = useCallback(async (companyId) => {
    try {
      const response = await axios.get(`${API}/form-builder/companies/${companyId}/meldungsarten`);
      if (companyRef.current !== companyId) return;
      setMeldungsarten(response.data);
      if (response.data.length > 0) {
        setSelectedMeldungsart(previous => response.data.find(m => m.id === previous?.id) || response.data[0]);
      } else {
        setSelectedMeldungsart(null);
        setSelectedSchema(null);
      }
    } catch (error) {
      console.error("Error:", error);
      setMeldungsarten([]);
      setSelectedMeldungsart(null);
      setSelectedSchema(null);
    }
  }, []);

  const fetchSchema = useCallback(async (companyId, meldungsartId) => {
    setSelectedSchema(null);
    try {
      const response = await axios.get(
        `${API}/form-builder/companies/${companyId}/schemas?meldungsart_id=${meldungsartId}`
      );
      if (response.data.length > 0) {
        const schemaResponse = await axios.get(
          `${API}/form-builder/companies/${companyId}/schemas/${response.data[0].id}`
        );
        if (companyRef.current === companyId && typeRef.current === meldungsartId) setSelectedSchema(schemaResponse.data);
      } else {
        setSelectedSchema(null);
      }
    } catch (error) {
      console.error("Error:", error);
      setSelectedSchema(null);
    }
  }, []);

  const fetchOptionLists = useCallback(async (companyId) => {
    try {
      const response = await axios.get(`${API}/form-builder/companies/${companyId}/option-lists`);
      if (companyRef.current === companyId) setOptionLists(response.data);
    } catch (error) {
      console.error("Error:", error);
      setOptionLists([]);
    }
  }, []);

  const refreshSchema = useCallback(async () => {
    if (selectedCompany && selectedSchema) {
      const response = await axios.get(
        `${API}/form-builder/companies/${selectedCompany.id}/schemas/${selectedSchema.id}`
      );
      if (companyRef.current === response.data.company_id && typeRef.current === response.data.meldungsart_id) setSelectedSchema(response.data);
    }
  }, [selectedCompany, selectedSchema]);

  useEffect(() => {
    fetchUserAndCompanies();
  }, [fetchUserAndCompanies]);

  useEffect(() => {
    if (selectedCompany) {
      fetchMeldungsarten(selectedCompany.id);
      fetchOptionLists(selectedCompany.id);
    }
  }, [selectedCompany, fetchMeldungsarten, fetchOptionLists]);

  useEffect(() => {
    if (selectedCompany && selectedMeldungsart) {
      fetchSchema(selectedCompany.id, selectedMeldungsart.id);
    }
  }, [selectedCompany, selectedMeldungsart, fetchSchema]);

  // Handlers
  const handleMigrateCompany = async () => {
    if (!selectedCompany) return;
    setMigrating(true);
    try {
      await axios.post(`${API}/form-builder/companies/${selectedCompany.id}/migrate-existing`);
      toast.success("Migration erfolgreich!");
      await fetchMeldungsarten(selectedCompany.id);
      await fetchOptionLists(selectedCompany.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Migration fehlgeschlagen");
    } finally {
      setMigrating(false);
    }
  };

  const handleCreateMeldungsart = async () => {
    if (!selectedCompany || !newMeldungsartName.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/form-builder/companies/${selectedCompany.id}/meldungsarten`, {
        name: newMeldungsartName,
        description: newMeldungsartDesc
      });
      toast.success("Meldungsart erstellt!");
      setNewMeldungsartDialog(false);
      setNewMeldungsartName("");
      setNewMeldungsartDesc("");
      await fetchMeldungsarten(selectedCompany.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMeldungsart = async (meldungsart, isActive) => {
    try {
      await axios.put(
        `${API}/form-builder/companies/${selectedCompany.id}/meldungsarten/${meldungsart.id}`,
        { is_active: isActive }
      );
      toast.success(isActive ? "Aktiviert" : "Deaktiviert");
      await fetchMeldungsarten(selectedCompany.id);
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const handleAddField = async () => {
    if (!selectedCompany || !selectedSchema || !newField.label.trim()) return;
    setSaving(true);
    try {
      const fieldKey = newField.field_key.trim() || 
        "custom_" + newField.label.toLowerCase().replace(/[^a-z0-9äöüß]/g, "_").replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss");
      
      const fieldData = {
        field_key: fieldKey,
        field_type: newField.field_type,
        label: newField.label,
        placeholder: newField.placeholder || null,
        help_text: newField.help_text || null,
        is_required: newField.is_required,
        is_visible: true,
        sort_order: (selectedSchema.fields?.length || 0)
      };

      // Für Auswahl-Felder
      if (["select", "multiselect", "radio"].includes(newField.field_type)) {
        if (newField.option_list_id) {
          fieldData.option_list_id = newField.option_list_id;
        } else if (newField.inline_options.trim()) {
          const opts = newField.inline_options.split("\n").filter(o => o.trim());
          fieldData.inline_options = opts.map((o, i) => ({ value: o.trim(), label: o.trim(), sort_order: i }));
        }
      }

      await axios.post(
        `${API}/form-builder/companies/${selectedCompany.id}/schemas/${selectedSchema.id}/fields`,
        fieldData
      );
      
      toast.success("Feld hinzugefügt!");
      setNewFieldDialog(false);
      setNewField({
        field_key: "",
        field_type: "text",
        label: "",
        placeholder: "",
        help_text: "",
        is_required: false,
        option_list_id: "",
        inline_options: ""
      });
      await refreshSchema();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateField = async () => {
    if (!selectedCompany || !selectedSchema || !editingField) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/form-builder/companies/${selectedCompany.id}/schemas/${selectedSchema.id}/fields/${editingField.id}`,
        {
          label: editingField.label,
          placeholder: editingField.placeholder,
          help_text: editingField.help_text,
          is_required: editingField.is_required,
          is_visible: editingField.is_visible,
          ...(["select", "multiselect", "radio"].includes(editingField.field_type) ? {
            option_list_id: editingField.option_list_id || null,
            inline_options: editingField.option_list_id ? null : editingField.optionsText.split("\n").map(o => o.trim()).filter(Boolean).map((label, index) => ({ ...(editingField.inline_options || []).find(o => o.label === label), label, value: (editingField.inline_options || []).find(o => o.label === label)?.value || label, sort_order: index }))
          } : {})
        }
      );
      toast.success("Entwurf gespeichert");
      setEditFieldDialog(false);
      setEditingField(null);
      await refreshSchema();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = async (field) => {
    if (!selectedCompany || !selectedSchema) return;
    if (field.is_system_field) {
      toast.error("System-Felder können nicht gelöscht werden");
      return;
    }
    if (!window.confirm(`"${field.label}" wirklich löschen?`)) return;
    
    try {
      await axios.delete(
        `${API}/form-builder/companies/${selectedCompany.id}/schemas/${selectedSchema.id}/fields/${field.id}`
      );
      toast.success("Feld gelöscht!");
      await refreshSchema();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    }
  };

  const handleMoveField = async (fieldId, direction) => {
    if (!selectedCompany || !selectedSchema) return;
    
    const fields = [...(selectedSchema.fields || [])].sort((a, b) => a.sort_order - b.sort_order);
    const currentIndex = fields.findIndex(f => f.id === fieldId);
    
    if (currentIndex === -1) return;
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === fields.length - 1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Felder tauschen
    [fields[currentIndex], fields[newIndex]] = [fields[newIndex], fields[currentIndex]];
    
    // Neue Reihenfolge an Backend senden
    const fieldOrder = fields.map(f => f.id);
    
    try {
      await axios.put(
        `${API}/form-builder/companies/${selectedCompany.id}/schemas/${selectedSchema.id}/reorder-fields`,
        { field_order: fieldOrder }
      );
      toast.success("Reihenfolge geändert");
      await refreshSchema();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Sortieren");
    }
  };

  const handleMoveMeldungsart = async (meldungsartId, direction) => {
    if (!selectedCompany) return;
    
    const sorted = [...meldungsarten].sort((a, b) => a.sort_order - b.sort_order);
    const currentIndex = sorted.findIndex(m => m.id === meldungsartId);
    
    if (currentIndex === -1) return;
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === sorted.length - 1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Tauschen
    [sorted[currentIndex], sorted[newIndex]] = [sorted[newIndex], sorted[currentIndex]];
    
    const meldungsartOrder = sorted.map(m => m.id);
    
    try {
      await axios.put(
        `${API}/form-builder/companies/${selectedCompany.id}/meldungsarten/reorder`,
        { meldungsart_order: meldungsartOrder }
      );
      toast.success("Reihenfolge geändert");
      await fetchMeldungsarten(selectedCompany.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Sortieren");
    }
  };

  const handlePublishSchema = async () => {
    if (!selectedCompany || !selectedSchema) return;
    setPublishing(true);
    try {
      const response = await axios.post(
        `${API}/form-builder/companies/${selectedCompany.id}/schemas/${selectedSchema.id}/publish`
      );
      toast.success(`Für ${selectedCompany.name} veröffentlicht · Version ${response.data.version}`);
      await refreshSchema();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setPublishing(false);
    }
  };

  const handleCreateOptionList = async () => {
    if (!selectedCompany || !newOptionList.name.trim()) return;
    setSaving(true);
    try {
      const optionsArray = newOptionList.options
        .split("\n")
        .map(o => o.trim())
        .filter(o => o.length > 0)
        .map((label, i) => ({ label, value: label, sort_order: i }));
      
      await axios.post(`${API}/form-builder/companies/${selectedCompany.id}/option-lists`, {
        name: newOptionList.name.toLowerCase().replace(/[^a-z0-9]/g, "_"),
        display_name: newOptionList.display_name || newOptionList.name,
        description: newOptionList.description,
        options: optionsArray
      });
      
      toast.success("Auswahlliste erstellt!");
      setNewOptionListDialog(false);
      setNewOptionList({ name: "", display_name: "", description: "", options: "" });
      await fetchOptionLists(selectedCompany.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOptionList = async () => {
    if (!selectedCompany || !editingOptionList) return;
    setSaving(true);
    try {
      const optionsArray = editingOptionList.optionsText
        .split("\n")
        .map(o => o.trim())
        .filter(o => o.length > 0)
        .map((label, i) => ({ ...editingOptionList.options?.find(o => o.label === label), label, value: editingOptionList.options?.find(o => o.label === label)?.value || label, sort_order: i, is_active: true }));
      
      await axios.put(
        `${API}/form-builder/companies/${selectedCompany.id}/option-lists/${editingOptionList.id}`,
        {
          display_name: editingOptionList.display_name,
          description: editingOptionList.description,
          options: optionsArray
        }
      );
      
      toast.success("Auswahlliste aktualisiert!");
      setEditOptionListDialog(false);
      setEditingOptionList(null);
      await fetchOptionLists(selectedCompany.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const openEditOptionList = (list) => {
    const optionsText = list.options?.map(o => o.label).join("\n") || "";
    setEditingOptionList({
      ...list,
      optionsText
    });
    setEditOptionListDialog(true);
  };

  const openEditFieldDialog = (field) => {
    const standardList = { abteilung: "abteilungen", koerperteil: "koerperteile", verletzungsart: "verletzungsarten", ursache: "ursachen" }[field.field_key];
    const linkedList = !field.option_list_id && field.inline_options == null && standardList ? optionLists.find(list => list.name === standardList) : null;
    const companyOptions = standardList ? selectedCompany?.[standardList === "abteilungen" ? standardList : `custom_${standardList}`] || [] : [];
    setEditingField({ ...field, option_list_id: field.option_list_id || linkedList?.id || null, optionsText: (field.inline_options || (linkedList ? linkedList.options : companyOptions.map(v => ({ label: v })))).map(o => o.label).join("\n") });
    setEditFieldDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-50 w-full min-w-0 form-builder-layout" data-testid="form-builder">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <Settings className="w-6 h-6 text-yellow-500" />
              <h1 className="text-xl font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Formulare & Einstellungen
              </h1>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 min-w-0 max-w-full">
              <Label className="text-zinc-400">Unternehmen:</Label>
              <Select
                value={selectedCompany?.id || ""}
                onValueChange={(id) => { setSelectedMeldungsart(null); setSelectedSchema(null); setMeldungsarten([]); setOptionLists([]); setSelectedCompany(companies.find(c => c.id === id)); }}
              >
                <SelectTrigger data-testid="builder-company-select" className="w-full sm:w-[250px] bg-zinc-900 border-zinc-700">
                  <SelectValue placeholder="Unternehmen wählen" />
                </SelectTrigger>
                <SelectContent data-testid="builder-company-options" className="bg-zinc-900 border-zinc-700">
                  {companies.map(company => (
                    <SelectItem data-testid={`builder-company-${company.id}`} key={company.id} value={company.id}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Migration Banner */}
        {selectedCompany && meldungsarten.length === 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
                <div>
                  <p className="font-medium text-yellow-500">Ersteinrichtung erforderlich</p>
                  <p className="text-sm text-zinc-400">
                    Bestehende Konfiguration muss übertragen werden.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleMigrateCompany}
                disabled={migrating}
                className="bg-yellow-500 text-black hover:bg-yellow-400"
                data-testid="migrate-btn"
              >
                {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {migrating ? "Migriere..." : "Jetzt einrichten"}
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex flex-wrap h-auto justify-start">
            <TabsTrigger data-testid="builder-types-tab" value="meldungsarten" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              Meldungsarten
            </TabsTrigger>
            <TabsTrigger data-testid="builder-fields-tab" value="felder" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Layers className="w-4 h-4 mr-2" />
              Formular-Editor
            </TabsTrigger>
            <TabsTrigger data-testid="builder-lists-tab" value="auswahllisten" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <List className="w-4 h-4 mr-2" />
              Auswahllisten
            </TabsTrigger>
            <TabsTrigger data-testid="builder-rules-tab" value="benutzer" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" />
              Benutzer-Regeln
            </TabsTrigger>
          </TabsList>

          {/* Meldungsarten Tab */}
          <TabsContent value="meldungsarten" className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <h2 className="text-lg font-semibold">Meldungsarten verwalten</h2>
              <Button
                onClick={() => setNewMeldungsartDialog(true)}
                disabled={!selectedCompany}
                className="bg-yellow-500 text-black hover:bg-yellow-400"
                data-testid="new-meldungsart-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Neue Meldungsart
              </Button>
            </div>

            <div className="grid gap-4">
              {[...meldungsarten].sort((a, b) => a.sort_order - b.sort_order).map((meldungsart, index, arr) => (
                <div
                  key={meldungsart.id}
                  className={`bg-zinc-900 border rounded-lg p-4 flex flex-wrap gap-3 items-center justify-between ${
                    meldungsart.is_active ? 'border-zinc-800' : 'border-zinc-800/50 opacity-60'
                  }`}
                  data-testid={`meldungsart-${meldungsart.internal_key}`}
                >
                  <div className="flex items-center gap-4 min-w-0 max-w-full">
                    <div className="flex flex-col gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleMoveMeldungsart(meldungsart.id, 'up')}
                        data-testid={`builder-type-up-${meldungsart.id}`}
                        disabled={index === 0}
                        title="Nach oben"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleMoveMeldungsart(meldungsart.id, 'down')}
                        data-testid={`builder-type-down-${meldungsart.id}`}
                        disabled={index === arr.length - 1}
                        title="Nach unten"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="min-w-0 break-words">
                      <h3 className="font-medium">{meldungsart.name}</h3>
                      {meldungsart.description && (
                        <p className="text-sm text-zinc-400">{meldungsart.description}</p>
                      )}
                      <p className="text-xs text-zinc-500 mt-1">
                        Schlüssel: {meldungsart.internal_key}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-zinc-400">Aktiv</Label>
                      <Switch
                        checked={meldungsart.is_active}
                        onCheckedChange={(checked) => handleToggleMeldungsart(meldungsart, checked)}
                        data-testid={`toggle-${meldungsart.internal_key}`}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedMeldungsart(meldungsart);
                        setActiveTab("felder");
                      }}
                      data-testid={`edit-${meldungsart.internal_key}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {meldungsarten.length === 0 && (
                <div className="text-center py-8 text-zinc-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Meldungsarten konfiguriert</p>
                  <p className="text-sm">Führen Sie die Ersteinrichtung durch</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Formular-Editor Tab */}
          <TabsContent value="felder" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex flex-wrap items-center gap-4 min-w-0 max-w-full">
                <h2 className="text-lg font-semibold">Formular-Editor</h2>
                
                <Select
                  value={selectedMeldungsart?.id || ""}
                  onValueChange={(id) => setSelectedMeldungsart(meldungsarten.find(m => m.id === id))}
                >
                  <SelectTrigger data-testid="builder-form-type-select" className="w-[200px] max-w-full bg-zinc-900 border-zinc-700">
                    <SelectValue placeholder="Meldungsart wählen" />
                  </SelectTrigger>
                  <SelectContent data-testid="builder-form-type-options" className="bg-zinc-900 border-zinc-700">
                    {meldungsarten.map(ma => (
                      <SelectItem data-testid={`builder-form-type-${ma.id}`} key={ma.id} value={ma.id}>{ma.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex flex-wrap items-center gap-2 max-w-full">
                <Button
                  variant="outline"
                  onClick={() => setPreviewDialog(true)}
                  disabled={!selectedSchema}
                  className="border-zinc-700"
                  data-testid="preview-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Vorschau
                </Button>
                <Button
                  onClick={handlePublishSchema}
                  disabled={!selectedSchema || publishing}
                  className="bg-yellow-500 text-black hover:bg-yellow-400"
                  data-testid="publish-btn"
                >
                  {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Veröffentlichen
                </Button>
              </div>
            </div>

            {selectedSchema ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
                  <div className="min-w-0 break-words max-w-full">
                    <h3 className="font-medium">{selectedSchema.name}</h3>
                    <p data-testid="builder-publication-status" className="text-sm text-zinc-400">
                      Version {selectedSchema.version || 1} • 
                      Status: {selectedSchema.status === 'published' ? 
                        <span className="text-green-500 ml-1">Veröffentlicht</span> : 
                        <span className="text-yellow-500 ml-1">Entwurf</span>
                      }
                    </p>
                  </div>
                  <Button
                    onClick={() => setNewFieldDialog(true)}
                    className="bg-zinc-800 hover:bg-zinc-700"
                    data-testid="add-field-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Feld hinzufügen
                  </Button>
                </div>

                <div className="space-y-2">
                  {[...(selectedSchema.fields || [])].sort((a, b) => a.sort_order - b.sort_order).map((field, index, arr) => (
                    <div
                      key={field.id}
                      className={`bg-zinc-950 border border-zinc-800 rounded p-3 flex flex-wrap gap-3 items-center justify-between ${
                        field.is_system_field ? 'border-l-4 border-l-blue-500' : ''
                      }`}
                      data-testid={`field-${field.field_key}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 max-w-full">
                        <div className="flex flex-col gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => handleMoveField(field.id, 'up')}
                            data-testid={`builder-field-up-${field.field_key}`}
                            disabled={index === 0}
                            title="Nach oben"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => handleMoveField(field.id, 'down')}
                            data-testid={`builder-field-down-${field.field_key}`}
                            disabled={index === arr.length - 1}
                            title="Nach unten"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="min-w-0 break-words">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{field.label}</span>
                            {field.is_required && <span className="text-red-500 text-xs">*Pflicht</span>}
                            {field.is_system_field && <span className="text-blue-500 text-xs">System</span>}
                          </div>
                          <p className="text-xs text-zinc-500">
                            {FIELD_TYPES[field.field_type] || field.field_type} • {field.field_key}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditFieldDialog(field)}
                          data-testid={`builder-field-edit-${field.field_key}`}
                          disabled={field.is_system_field}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteField(field)}
                          data-testid={`builder-field-delete-${field.field_key}`}
                          disabled={field.is_system_field}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {(!selectedSchema.fields || selectedSchema.fields.length === 0) && (
                    <div className="text-center py-8 text-zinc-500">
                      <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Keine Felder konfiguriert</p>
                    </div>
                  )}
                </div>
              </div>
            ) : selectedMeldungsart ? (
              <div className="text-center py-12 text-zinc-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Kein Schema für diese Meldungsart</p>
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Wählen Sie eine Meldungsart</p>
              </div>
            )}
          </TabsContent>

          {/* Auswahllisten Tab */}
          <TabsContent value="auswahllisten" className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <h2 className="text-lg font-semibold">Auswahllisten verwalten</h2>
              <Button
                onClick={() => setNewOptionListDialog(true)}
                disabled={!selectedCompany}
                className="bg-yellow-500 text-black hover:bg-yellow-400"
                data-testid="new-option-list-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Neue Liste
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {optionLists.map(list => (
                <div
                  key={list.id}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-4"
                  data-testid={`option-list-${list.name}`}
                >
                  <div className="flex flex-wrap gap-3 items-center justify-between mb-3">
                    <h3 className="font-medium">{list.display_name}</h3>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditOptionList(list)}
                      data-testid={`edit-option-list-${list.name}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500 mb-2">
                    Schlüssel: {list.name} • {list.options?.length || 0} Optionen
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {list.options?.slice(0, 5).map(opt => (
                      <span
                        key={opt.id || opt.value}
                        className={`text-xs px-2 py-1 rounded ${
                          opt.is_active !== false ? 'bg-zinc-800' : 'bg-zinc-800/50 line-through'
                        }`}
                      >
                        {opt.label}
                      </span>
                    ))}
                    {list.options?.length > 5 && (
                      <span className="text-xs text-zinc-500">
                        +{list.options.length - 5} weitere
                      </span>
                    )}
                  </div>
                </div>
              ))}
              
              {optionLists.length === 0 && (
                <div className="col-span-full text-center py-8 text-zinc-500">
                  <List className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Auswahllisten</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Benutzer-Regeln Tab */}
          <TabsContent value="benutzer" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Benutzer- und Rollenregeln</h2>
            </div>
            
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
              <div className="text-center text-zinc-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="mb-2">Benutzer- und Rollenregeln</p>
                <p className="text-sm">
                  Noch nicht verfügbar.
                </p>
                <p className="text-xs mt-4 text-zinc-600">
                  Aktuell gelten die veröffentlichten Unternehmenseinstellungen.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog: Neue Meldungsart */}
      <Dialog open={newMeldungsartDialog} onOpenChange={setNewMeldungsartDialog}>
        <DialogContent data-testid="builder-new-type-dialog" className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Neue Meldungsart erstellen</DialogTitle>
            <DialogDescription>Erstellen Sie eine neue Meldungsart.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newMeldungsartName}
                onChange={(e) => setNewMeldungsartName(e.target.value)}
                placeholder="z.B. Arbeitsunfall"
                className="bg-zinc-950 border-zinc-700"
                data-testid="meldungsart-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Beschreibung</Label>
              <Textarea
                value={newMeldungsartDesc}
                data-testid="builder-new-type-description"
                onChange={(e) => setNewMeldungsartDesc(e.target.value)}
                placeholder="Optionale Beschreibung..."
                className="bg-zinc-950 border-zinc-700"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button data-testid="builder-new-type-cancel" variant="outline" onClick={() => setNewMeldungsartDialog(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleCreateMeldungsart}
                disabled={saving || !newMeldungsartName.trim()}
                className="bg-yellow-500 text-black hover:bg-yellow-400"
                data-testid="create-meldungsart-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Erstellen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Neues Feld */}
      <Dialog open={newFieldDialog} onOpenChange={setNewFieldDialog}>
        <DialogContent data-testid="builder-new-field-dialog" className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Neues Feld hinzufügen</DialogTitle>
            <DialogDescription>Fügen Sie ein neues Feld zum Formular hinzu.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Bezeichnung *</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField({...newField, label: e.target.value})}
                placeholder="z.B. Arbeitsbereich"
                className="bg-zinc-950 border-zinc-700"
                data-testid="field-label-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Feldtyp *</Label>
              <Select
                value={newField.field_type}
                onValueChange={(value) => setNewField({...newField, field_type: value})}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="field-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent data-testid="builder-field-type-options" className="bg-zinc-950 border-zinc-700">
                  {Object.entries(FIELD_TYPES).map(([key, label]) => (
                    <SelectItem data-testid={`builder-field-type-${key}`} key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Technischer Schlüssel</Label>
              <Input
                value={newField.field_key}
                data-testid="builder-new-field-key"
                onChange={(e) => setNewField({...newField, field_key: e.target.value})}
                placeholder="Wird automatisch generiert"
                className="bg-zinc-950 border-zinc-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Platzhalter-Text</Label>
              <Input
                value={newField.placeholder}
                data-testid="builder-new-field-placeholder"
                onChange={(e) => setNewField({...newField, placeholder: e.target.value})}
                placeholder="Hinweis im leeren Feld"
                className="bg-zinc-950 border-zinc-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Hilfetext</Label>
              <Textarea
                value={newField.help_text}
                data-testid="builder-new-field-help"
                onChange={(e) => setNewField({...newField, help_text: e.target.value})}
                placeholder="Zusätzliche Erklärung"
                className="bg-zinc-950 border-zinc-700"
                rows={2}
              />
            </div>
            
            {['select', 'multiselect', 'radio'].includes(newField.field_type) && (
              <>
                <div className="space-y-2">
                  <Label>Auswahlliste verwenden</Label>
                  <Select
                    value={newField.option_list_id || "none"}
                    onValueChange={(value) => setNewField({...newField, option_list_id: value === "none" ? "" : value})}
                  >
                    <SelectTrigger data-testid="builder-new-field-list" className="bg-zinc-950 border-zinc-700">
                      <SelectValue placeholder="Keine (eigene Optionen)" />
                    </SelectTrigger>
                    <SelectContent data-testid="builder-new-field-list-options" className="bg-zinc-950 border-zinc-700">
                      <SelectItem data-testid="builder-new-field-list-none" value="none">Keine (eigene Optionen)</SelectItem>
                      {optionLists.map(list => (
                        <SelectItem data-testid={`builder-new-field-list-${list.id}`} key={list.id} value={list.id}>{list.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {(!newField.option_list_id || newField.option_list_id === "none") && (
                  <div className="space-y-2">
                    <Label>Eigene Optionen (eine pro Zeile)</Label>
                    <Textarea
                      value={newField.inline_options}
                      onChange={(e) => setNewField({...newField, inline_options: e.target.value})}
                      placeholder={"Option 1\nOption 2\nOption 3"}
                      className="bg-zinc-950 border-zinc-700"
                      rows={4}
                      data-testid="inline-options-input"
                    />
                  </div>
                )}
              </>
            )}
            
            <div className="flex items-center gap-2">
              <Switch
                checked={newField.is_required}
                onCheckedChange={(checked) => setNewField({...newField, is_required: checked})}
                data-testid="field-required-switch"
              />
              <Label>Pflichtfeld</Label>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button data-testid="builder-new-field-cancel" variant="outline" onClick={() => setNewFieldDialog(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleAddField}
                disabled={saving || !newField.label.trim()}
                className="bg-yellow-500 text-black hover:bg-yellow-400"
                data-testid="create-field-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Hinzufügen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Feld bearbeiten */}
      <Dialog open={editFieldDialog} onOpenChange={setEditFieldDialog}>
        <DialogContent data-testid="builder-edit-field-dialog" className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Feld bearbeiten</DialogTitle>
            <DialogDescription>Bearbeiten Sie die Feldeinstellungen.</DialogDescription>
          </DialogHeader>
          
          {editingField && (
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Bezeichnung *</Label>
                <Input
                  value={editingField.label}
                  data-testid="builder-edit-field-label"
                  onChange={(e) => setEditingField({...editingField, label: e.target.value})}
                  className="bg-zinc-950 border-zinc-700"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Platzhalter-Text</Label>
                <Input
                  value={editingField.placeholder || ""}
                  data-testid="builder-edit-field-placeholder"
                  onChange={(e) => setEditingField({...editingField, placeholder: e.target.value})}
                  className="bg-zinc-950 border-zinc-700"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Hilfetext</Label>
                <Textarea
                  value={editingField.help_text || ""}
                  data-testid="builder-edit-field-help"
                  onChange={(e) => setEditingField({...editingField, help_text: e.target.value})}
                  className="bg-zinc-950 border-zinc-700"
                  rows={2}
                />
              </div>
              
              {["select", "multiselect", "radio"].includes(editingField.field_type) && <div className="space-y-3">
                <Label>Auswahlliste</Label>
                <Select value={editingField.option_list_id || "none"} onValueChange={value => setEditingField({ ...editingField, option_list_id: value === "none" ? null : value })}>
                  <SelectTrigger data-testid="builder-edit-field-list" className="bg-zinc-950 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent data-testid="builder-edit-field-list-options" className="bg-zinc-950 border-zinc-700"><SelectItem data-testid="builder-edit-field-list-none" value="none">Eigene Optionen</SelectItem>{optionLists.map(list => <SelectItem data-testid={`builder-edit-field-list-${list.id}`} key={list.id} value={list.id}>{list.display_name}</SelectItem>)}</SelectContent>
                </Select>
                {!editingField.option_list_id && <Textarea data-testid="builder-edit-field-options" aria-label="Eigene Optionen" value={editingField.optionsText} onChange={e => setEditingField({ ...editingField, optionsText: e.target.value })} rows={4} placeholder="Eine Option pro Zeile" className="bg-zinc-950 border-zinc-700" />}
              </div>}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingField.is_required}
                    data-testid="builder-edit-field-required"
                    onCheckedChange={(checked) => setEditingField({...editingField, is_required: checked})}
                  />
                  <Label>Pflichtfeld</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingField.is_visible !== false}
                    data-testid="builder-edit-field-visible"
                    onCheckedChange={(checked) => setEditingField({...editingField, is_visible: checked})}
                  />
                  <Label>Sichtbar</Label>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button data-testid="builder-edit-field-cancel" variant="outline" onClick={() => setEditFieldDialog(false)}>
                  Abbrechen
                </Button>
                <Button
                  onClick={handleUpdateField}
                  data-testid="builder-edit-field-save"
                  disabled={saving}
                  className="bg-yellow-500 text-black hover:bg-yellow-400"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Neue Auswahlliste */}
      <Dialog open={newOptionListDialog} onOpenChange={setNewOptionListDialog}>
        <DialogContent data-testid="builder-new-list-dialog" className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Neue Auswahlliste erstellen</DialogTitle>
            <DialogDescription>Erstellen Sie eine neue Auswahlliste.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newOptionList.name}
                onChange={(e) => setNewOptionList({...newOptionList, name: e.target.value})}
                placeholder="z.B. Arbeitsbereiche"
                className="bg-zinc-950 border-zinc-700"
                data-testid="option-list-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Anzeigename</Label>
              <Input
                value={newOptionList.display_name}
                data-testid="builder-new-list-display-name"
                onChange={(e) => setNewOptionList({...newOptionList, display_name: e.target.value})}
                placeholder="z.B. Arbeitsbereiche"
                className="bg-zinc-950 border-zinc-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Optionen (eine pro Zeile) *</Label>
              <Textarea
                value={newOptionList.options}
                onChange={(e) => setNewOptionList({...newOptionList, options: e.target.value})}
                placeholder={"Option 1\nOption 2\nOption 3"}
                className="bg-zinc-950 border-zinc-700"
                rows={6}
                data-testid="option-list-options-input"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button data-testid="builder-new-list-cancel" variant="outline" onClick={() => setNewOptionListDialog(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleCreateOptionList}
                disabled={saving || !newOptionList.name.trim() || !newOptionList.options.trim()}
                className="bg-yellow-500 text-black hover:bg-yellow-400"
                data-testid="create-option-list-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Erstellen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Auswahlliste bearbeiten */}
      <Dialog open={editOptionListDialog} onOpenChange={setEditOptionListDialog}>
        <DialogContent data-testid="builder-edit-list-dialog" className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Auswahlliste bearbeiten</DialogTitle>
            <DialogDescription>Bearbeiten Sie die Auswahlliste "{editingOptionList?.display_name}".</DialogDescription>
          </DialogHeader>
          
          {editingOptionList && (
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Anzeigename</Label>
                <Input
                  value={editingOptionList.display_name}
                  onChange={(e) => setEditingOptionList({...editingOptionList, display_name: e.target.value})}
                  className="bg-zinc-950 border-zinc-700"
                  data-testid="edit-option-list-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Input
                  value={editingOptionList.description || ""}
                  data-testid="builder-edit-list-description"
                  onChange={(e) => setEditingOptionList({...editingOptionList, description: e.target.value})}
                  className="bg-zinc-950 border-zinc-700"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Optionen (eine pro Zeile)</Label>
                <Textarea
                  value={editingOptionList.optionsText}
                  onChange={(e) => setEditingOptionList({...editingOptionList, optionsText: e.target.value})}
                  className="bg-zinc-950 border-zinc-700"
                  rows={8}
                  data-testid="edit-option-list-options"
                />
                <p className="text-xs text-zinc-500">
                  {editingOptionList.optionsText.split("\n").filter(o => o.trim()).length} Optionen
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button data-testid="builder-edit-list-cancel" variant="outline" onClick={() => setEditOptionListDialog(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Abbrechen
                </Button>
                <Button
                  onClick={handleUpdateOptionList}
                  disabled={saving}
                  className="bg-yellow-500 text-black hover:bg-yellow-400"
                  data-testid="save-option-list-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Vorschau */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent data-testid="builder-preview-dialog" className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Formular-Vorschau</DialogTitle>
            <DialogDescription>So wird das Formular angezeigt.</DialogDescription>
          </DialogHeader>
          
          {selectedSchema && (
            <div className="space-y-4 pt-4">
              {selectedSchema.fields?.sort((a, b) => a.sort_order - b.sort_order).map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label>
                    {field.label}
                    {field.is_required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  
                  {field.field_type === 'text' && (
                    <Input data-testid={`builder-preview-${field.field_key}`} placeholder={field.placeholder} className="bg-zinc-950 border-zinc-700" disabled />
                  )}
                  {field.field_type === 'textarea' && (
                    <Textarea data-testid={`builder-preview-${field.field_key}`} placeholder={field.placeholder} className="bg-zinc-950 border-zinc-700" disabled />
                  )}
                  {field.field_type === 'number' && (
                    <Input data-testid={`builder-preview-${field.field_key}`} type="number" placeholder={field.placeholder} className="bg-zinc-950 border-zinc-700" disabled />
                  )}
                  {field.field_type === 'date' && (
                    <Input data-testid={`builder-preview-${field.field_key}`} type="date" className="bg-zinc-950 border-zinc-700" disabled />
                  )}
                  {field.field_type === 'boolean' && (
                    <div className="flex items-center gap-2">
                      <Switch data-testid={`builder-preview-${field.field_key}`} disabled />
                      <span className="text-zinc-400">Ja/Nein</span>
                    </div>
                  )}
                  {['select', 'multiselect', 'radio'].includes(field.field_type) && (
                    <Select disabled>
                      <SelectTrigger data-testid={`builder-preview-${field.field_key}`} className="bg-zinc-950 border-zinc-700">
                        <SelectValue placeholder={field.field_type === 'multiselect' ? "Mehrere auswählen..." : "Auswählen..."} />
                      </SelectTrigger>
                    </Select>
                  )}
                  
                  {field.help_text && (
                    <p className="text-xs text-zinc-500">{field.help_text}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
